import {
  CheckIcon,
  InformationCircleIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormFieldError, TextInput } from '@kira/checkpoint-shared-frontend';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import * as yup from 'yup';

import useHover from 'hooks/useHover';
import useNotification from 'hooks/useNotification';
import useOnClickOutside from 'hooks/useOnClickOutside';
import useUpdateCompetency from 'hooks/useUpdateCompetency';
import { t } from 'locales/i18n';
import { AssessmentDetailsContext } from 'providers/AssessmentDetailsProvider';

interface CompetencyTitleProps {
  competencyName: string;
  questionsCount: number;
  pointScale: number;
  isQuestion: boolean;
}
enum FormFields {
  COMPETENCY_NAME = 'name',
}

interface FormData {
  [FormFields.COMPETENCY_NAME]: string;
}

const CompetencyTitle = ({
  competencyName,
  questionsCount,
  pointScale,
  isQuestion,
}: CompetencyTitleProps) => {
  const [showForm, setShowForm] = useState(false);
  const [hoverRef, isHovered] = useHover();
  const { interviewUid = '', competencyUid = '' } = useParams();
  const { updateCompetency } = useUpdateCompetency(interviewUid);
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { isPractice } = useContext(AssessmentDetailsContext);

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.COMPETENCY_NAME]: yup
          .string()
          .required(t('Name is required'))
          .max(50, t('Competency name cannot exceed 50 characters')),
      }),
    [FormFields.COMPETENCY_NAME]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    defaultValues: {
      [FormFields.COMPETENCY_NAME]: competencyName,
    },
  });

  useEffect(() => {
    reset({
      [FormFields.COMPETENCY_NAME]: competencyName,
    });
    setShowForm(false);
  }, [competencyUid]);

  const onSubmit = ({ name }: FormData) => {
    updateCompetency(
      { competencyUid, name },
      {
        onSuccess: () => {
          setShowForm(false);
          showSuccessNotification(t('Success'), t('Competency name updated'));
        },
        onError: (error) => {
          showErrorNotification(t('Error'), error.message);
        },
      }
    );
  };

  const handleCancel = () => {
    setShowForm(false);
    reset({
      [FormFields.COMPETENCY_NAME]: competencyName,
    });
  };

  useOnClickOutside(hoverRef, handleCancel);

  const titleWrapperClassName = twMerge(
    'flex',
    'flex-col',
    'md:flex-row',
    'items-center',
    'justify-between',
    'w-full',
    'border-b',
    'border-kira-slate-200',
    'pl-5'
  );

  const metricTitleClassName = twMerge(
    'text-center',
    'md:text-right',
    'prose-title-h1'
  );

  const metricSubtitleClassName = twMerge(
    'text-center',
    'md:text-right',
    'prose-body-sm-reg',
    'text-kira-slate-800'
  );

  const titleClassNames = twMerge(
    'prose-title-h1',
    'bg-inherit',
    'text-kira-slate-900',
    'border-transparent',
    'p-1',
    'w-full',
    'flex',
    'break-words',
    'justify-between',
    'hover:cursor-pointer',
    'hover:rounded',
    'hover:border',
    'hover:border-kira-blue-800',
    'hover:bg-kira-blue-100',
    !showForm && 'cursor-pointer'
  );

  const formWrapperClassNames = twMerge(
    'flex',
    'items-center',
    'justify-between',
    showForm && 'border-b border-kira-blue-800'
  );

  const icon = <InformationCircleIcon className="mr-1 inline-block h-4 w-4" />;

  return (
    <div className={titleWrapperClassName}>
      <form
        data-testid="competency-update-name-form"
        onSubmit={handleSubmit(onSubmit)}
        className="w-full"
      >
        <div className={formWrapperClassNames} ref={hoverRef}>
          {!showForm && (
            <div
              className={titleClassNames}
              onClick={() => {
                setShowForm(true);
              }}
              aria-hidden
            >
              <div className="break-words" data-testid="competencyName">
                {competencyName}
              </div>
              {isHovered && (
                <div className="flex items-center pr-5">
                  <PencilIcon className="h-3.5 w-3.5 text-kira-blue-800" />
                  <div className="prose-button-sm pl-1 text-kira-blue-800">
                    {t('Edit')}
                  </div>
                </div>
              )}
            </div>
          )}
          {showForm && (
            <div className="flex w-full flex-col">
              <div className="flex w-full ">
                <TextInput
                  id={FormFields.COMPETENCY_NAME}
                  data-testid="competencyName"
                  inputClassName="bg-inherit !border-transparent !prose-title-h1 !p-1"
                  wrapperClassName="w-full"
                  value={watch(FormFields.COMPETENCY_NAME)}
                  hasError={!!errors[FormFields.COMPETENCY_NAME]}
                  {...register(FormFields.COMPETENCY_NAME)}
                  autoFocus
                />
                <div className="flex gap-2 pr-2">
                  <button type="submit" data-testid="submit">
                    <CheckIcon className="h-8 w-8 rounded bg-kira-green-800 p-2 text-white" />
                  </button>
                  <button
                    onClick={handleCancel}
                    type="button"
                    data-testid="cancel"
                  >
                    <XMarkIcon className="h-8 w-8 rounded bg-kira-slate-700 p-2 text-white" />
                  </button>
                </div>
              </div>
              <FormFieldError
                isVisible={!!errors[FormFields.COMPETENCY_NAME]}
                message={errors[FormFields.COMPETENCY_NAME]?.message ?? ''}
                className="-mb-6"
                icon={icon}
              />
            </div>
          )}
        </div>
      </form>
      <div className="flex w-max justify-end">
        {isQuestion && (
          <div className="pb-3">
            <p className={metricTitleClassName}>{questionsCount}</p>
            <p className={metricSubtitleClassName}>
              {t('Question', { count: questionsCount })}
            </p>
          </div>
        )}
        {!isPractice && (
          <div className="ml-8 w-max pb-3">
            <p className={metricTitleClassName}>{pointScale}</p>
            <p className={metricSubtitleClassName}>{t('Point Scale')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetencyTitle;
